/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import { FiCheck } from 'react-icons/fi';
import { useSignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { BigButton } from '@/components/big-button';
import { VSpace } from '@/components/spacing';
import { DatePickerContext } from '@/features/details/date-pick/context';

import { isValid } from '@/utils/data_freshness';
import { maybe } from '@/features/details/utils';
import { breakpoints } from '@/components/responsive';

export enum Opt {
  Current,
  Today,
  Yesterday,
  LastWeek,
  LastMonth,
  CustomRange,
  OverAll,
  PreviousVersion,
}

type Props = {
  close: () => void;
};
export const QuickRangePicker: FC<Props> = ({ close }) => {
  useSignals();
  const {
    versions,
    step: active,
    setStep: setActive,
    customRange,
    quickSelect,
    versionsSelect,
    startDate,
    endDate,
    version,
  } = useContext(DatePickerContext);
  const selected = useSignal(active?.peek());
  const days = maybe(
    () => countCalendarDays(startDate.value, endDate.value),
    countDays(versions?.find((v) => v.isCurrent)),
  );
  return (
    <Container>
      <div>
        <Option
          isActive={selected.value === Opt.Current}
          onClick={() => {
            selected.value = Opt.Current;
          }}
        >
          Latest Version {version?.value?.isCurrent && `(${days} days)`}
        </Option>
        <Option
          isActive={selected.value === Opt.Today}
          onClick={() => {
            selected.value = Opt.Today;
          }}
        >
          Today
        </Option>
        <Option
          isActive={selected.value === Opt.Yesterday}
          onClick={() => {
            selected.value = Opt.Yesterday;
          }}
        >
          Yesterday
        </Option>
        <Option
          isActive={selected.value === Opt.LastWeek}
          onClick={() => {
            selected.value = Opt.LastWeek;
          }}
        >
          Last Week
        </Option>
        <Option
          isActive={selected.value === Opt.LastMonth}
          onClick={() => {
            selected.value = Opt.LastMonth;
          }}
        >
          Last Month
        </Option>
        <Option
          isActive={selected.value === Opt.CustomRange}
          onClick={() => {
            selected.value = Opt.CustomRange;
            setActive(Opt.CustomRange);
            customRange();
          }}
        >
          Custom Range
        </Option>
        {versions?.length > 1 && (
          <Option
            isActive={selected.value === Opt.OverAll}
            onClick={() => {
              selected.value = Opt.OverAll;
            }}
          >
            Overall
          </Option>
        )}
        {versions?.length > 1 && (
          <Option
            isActive={selected.value === Opt.PreviousVersion}
            onClick={() => {
              selected.value = Opt.PreviousVersion;
              setActive(Opt.PreviousVersion);
              versionsSelect();
            }}
          >
            Previous Versions ({versions.length})
          </Option>
        )}
      </div>
      <VSpace value={2} />
      <BigButton
        onClick={() => {
          setActive(selected.value);
          if (selected.value === Opt.Current) {
            quickSelect();
          }
          close();
        }}
        background="blue"
        noTransform
        size="medium"
        fillWidth
      >
        Apply
      </BigButton>
      <VSpace />
    </Container>
  );
};

const Item = styled.button`
  display: flex;
  align-items: center;
  margin: 10px 0;
  color: ${(p: { isActive: boolean }) => (p.isActive ? `blue` : `#60686e`)};
  font-size: 14px;
  font-weight: 500;
  user-select: none;
  :hover {
    cursor: pointer;
    opacity: 0.5;
  }
  :active {
    opacity: 1;
  }
  svg {
    margin-right: 1rem;
  }
`;

export const Option: FC<{
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ children, onClick, isActive }) => (
  <Item onClick={onClick} isActive={isActive}>
    {isActive && <FiCheck />}
    {children}
  </Item>
);

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;

  background-color: white;
  border-radius: 15px;
  width: 400px;
  padding: 1rem 3rem;
  border: solid 0.6px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
  font-family: 'JetBrains Mono', serif;
  max-height: 80vh;

  && > div {
    overflow: scroll;
    display: flex;
    flex-direction: column;
    -webkit-box-pack: start;
    justify-content: start;
    -webkit-box-align: start;
    align-items: start;
    font-family: 'JetBrains Mono', serif;
  }

  ${breakpoints.down(`md`)} {
    width: 100%;
    border-radius: 2rem 2rem 0 0;
  }
`;

export function countDays(version?: ExpVersion): number {
  let endDate = new Date(version?.endDate);
  if (version?.isCurrent && version?.expEndDate) {
    endDate = version.expEndDate;
  }
  const startDate = new Date(version?.startDate);
  if (!isValid(startDate) || !isValid(endDate)) {
    return 1;
  }
  return countCalendarDays(startDate, endDate);
}

const MS_PER_DAY = 8.64e7;

function countCalendarDays(startDate: Date, endDate: Date): number {
  return utcDayIndex(endDate) - utcDayIndex(startDate) + 1;
}

function utcDayIndex(date: Date): number {
  return Math.floor(date.getTime() / MS_PER_DAY);
}
